import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  ClipboardCheck, Loader2, RefreshCw, CheckCircle2, XCircle,
  MapPin, ScanLine, User, FileText, Clock, Warehouse, Package,
  Search as SearchIcon, X,
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { useAdminAuth, API } from '../context/AdminAuthContext';
import { useToast } from '../hooks/use-toast';

/**
 * Feb 2026 — Super-admin Audit Queue.
 *
 * Lists every move request submitted from Floor Mode > Audit. Super
 * admin reviews source + target + requester note, then approves
 * (runs the actual pallet move via `_apply_transfer_stock_move`) or
 * rejects (writes a decision note). Both outcomes push a bell + web
 * notification to the requester.
 *
 * Route: /admin/audit-queue
 */

const STATUS_TABS = [
  { key: 'pending', label: 'Pending', color: 'amber' },
  { key: 'approved', label: 'Approved', color: 'emerald' },
  { key: 'rejected', label: 'Rejected', color: 'rose' },
  { key: 'all', label: 'All', color: 'slate' },
];

const StatusChip = ({ status }) => {
  const map = {
    pending: { cls: 'bg-amber-100 text-amber-900', Icon: Clock },
    approved: { cls: 'bg-emerald-100 text-emerald-800', Icon: CheckCircle2 },
    rejected: { cls: 'bg-rose-100 text-rose-700', Icon: XCircle },
  };
  const { cls, Icon } = map[status] || map.pending;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${cls}`}>
      <Icon className="w-3 h-3" /> {status || 'pending'}
    </span>
  );
};

const fmtDt = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: '2-digit', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
};


const RejectModal = ({ row, API, authHeaders, onClose, onDone }) => {
  const { toast } = useToast();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/audit-queue/${encodeURIComponent(row.id)}/reject`,
        { decision_note: note.trim() },
        { headers: authHeaders },
      );
      toast({ title: 'Request rejected', description: 'The requester has been notified.' });
      onDone?.();
      onClose();
    } catch (e) {
      toast({
        title: 'Reject failed',
        description: e?.response?.data?.detail || e?.message || '',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl"
        data-testid="audit-reject-modal"
      >
        <div className="p-4 border-b border-slate-100">
          <div className="text-[11px] font-bold uppercase tracking-wider text-rose-600">Reject move request</div>
          <div className="text-[15px] font-bold text-[#0F172A] leading-tight mt-0.5">
            {row.pallet_id || row.product_name || 'Pallet'} · {row.from_location_id} → {row.to_location_id}
          </div>
        </div>
        <div className="p-4">
          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Reason (optional)</span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="e.g. Target bin is full; please pick another one."
              className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-slate-200 bg-white text-[13px] focus:outline-none focus:border-rose-500 resize-none"
              data-testid="audit-reject-note"
            />
          </label>
          <div className="mt-2 text-[11px] text-slate-500">
            The requester will see this reason in their notification.
          </div>
        </div>
        <div className="p-3 border-t border-slate-100 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-[13px]"
            data-testid="audit-reject-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="flex-[1.4] h-10 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-bold text-[13px] inline-flex items-center justify-center gap-1.5 disabled:opacity-70"
            data-testid="audit-reject-confirm"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
            {busy ? 'Rejecting…' : 'Reject request'}
          </button>
        </div>
      </div>
    </div>
  );
};


const AdminAuditQueue = () => {
  const { authHeaders } = useAdminAuth();
  const { toast } = useToast();
  const [status, setStatus] = useState('pending');
  const [rows, setRows] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [approving, setApproving] = useState('');
  const [rejectFor, setRejectFor] = useState(null);
  const [filter, setFilter] = useState('');

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/admin/audit-queue`, {
        headers: authHeaders,
        params: { status, limit: 200 },
      });
      setRows(Array.isArray(data?.results) ? data.results : []);
      setPendingCount(Number(data?.pending_count || 0));
    } catch (e) {
      toast({
        title: 'Could not load the queue',
        description: e?.response?.data?.detail || e?.message || '',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => (
      (r.pallet_id || '').toLowerCase().includes(q)
      || (r.product_name || '').toLowerCase().includes(q)
      || (r.product_sku || '').toLowerCase().includes(q)
      || (r.from_location_id || '').toLowerCase().includes(q)
      || (r.to_location_id || '').toLowerCase().includes(q)
      || (r.requested_by || '').toLowerCase().includes(q)
      || (r.requested_by_name || '').toLowerCase().includes(q)
    ));
  }, [rows, filter]);

  const approve = async (row) => {
    setApproving(row.id);
    try {
      await axios.post(
        `${API}/admin/audit-queue/${encodeURIComponent(row.id)}/approve`,
        {},
        { headers: authHeaders },
      );
      toast({ title: 'Move approved', description: 'Pallet relocated — requester notified.' });
      load();
    } catch (e) {
      toast({
        title: 'Approve failed',
        description: e?.response?.data?.detail || e?.message || '',
        variant: 'destructive',
      });
    } finally {
      setApproving('');
    }
  };

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-4" data-testid="audit-queue-page">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <ClipboardCheck className="w-6 h-6 text-amber-600" />
              <h1 className="text-xl sm:text-2xl font-extrabold text-[#0F172A]">Audit Queue</h1>
              {pendingCount > 0 && (
                <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-amber-500 text-white px-2.5 py-0.5 text-[12px] font-bold" data-testid="audit-queue-pending-badge">
                  <Clock className="w-3 h-3" /> {pendingCount} pending
                </span>
              )}
            </div>
            <p className="mt-1 text-[13px] text-slate-500 max-w-xl">
              Review every move request raised from Floor Mode &gt; Audit. Approving relocates the pallet
              immediately (inventory, bin occupancy, stock levels and ledger all update together) and notifies the requester.
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            disabled={busy}
            className="h-10 px-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-bold text-[13px] inline-flex items-center gap-1.5"
            data-testid="audit-queue-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        {/* Status tabs */}
        <div className="flex flex-wrap gap-1.5" data-testid="audit-queue-tabs">
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setStatus(t.key)}
              className={`px-3 py-1.5 rounded-full text-[12px] font-bold border transition ${
                status === t.key
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
              }`}
              data-testid={`audit-queue-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Filter box */}
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Filter by pallet, product, location, or requester…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full h-10 pl-10 pr-10 rounded-xl border border-slate-200 bg-white text-[13px] focus:outline-none focus:border-amber-500"
            data-testid="audit-queue-filter"
          />
          {filter && (
            <button
              type="button"
              onClick={() => setFilter('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-lg hover:bg-slate-100 text-slate-400"
            >
              <X className="w-4 h-4 mx-auto" />
            </button>
          )}
        </div>

        {/* List */}
        {busy && rows.length === 0 ? (
          <div className="py-20 flex items-center justify-center text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center" data-testid="audit-queue-empty">
            <ClipboardCheck className="w-12 h-12 mx-auto text-slate-300" />
            <div className="mt-2 text-[13px] font-semibold text-slate-500">
              No {status === 'all' ? '' : status} requests.
            </div>
          </div>
        ) : (
          <div className="space-y-2.5" data-testid="audit-queue-list">
            {filtered.map((r) => (
              <div
                key={r.id}
                className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden"
                data-testid={`audit-queue-row-${r.id}`}
              >
                <div className="p-4 flex flex-col sm:flex-row sm:items-start gap-4">
                  {/* Left — product + requester + note */}
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex items-center gap-2">
                      <StatusChip status={r.status} />
                      <span className="text-[11px] text-slate-400 font-semibold">{fmtDt(r.requested_at)}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Package className="w-4 h-4 text-slate-400 mt-0.5" />
                      <div>
                        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                          {r.product_sku || '—'}
                        </div>
                        <div className="text-[14px] font-bold text-[#0F172A] leading-tight" data-testid={`audit-queue-product-${r.id}`}>
                          {r.product_name || 'Unknown product'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-[12px] text-slate-600">
                      <User className="w-4 h-4 text-slate-400" />
                      <span className="font-semibold">{r.requested_by_name || r.requested_by}</span>
                      {r.requested_by_name && r.requested_by && (
                        <span className="text-slate-400">· {r.requested_by}</span>
                      )}
                    </div>
                    {r.note && (
                      <div className="rounded-lg bg-slate-50 border border-slate-100 p-2 flex items-start gap-2 text-[12px] text-slate-700">
                        <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
                        <span className="whitespace-pre-wrap">{r.note}</span>
                      </div>
                    )}
                    {r.decision_note && r.status === 'rejected' && (
                      <div className="rounded-lg bg-rose-50 border border-rose-200 p-2 text-[12px] text-rose-800">
                        <span className="font-bold">Reason: </span>{r.decision_note}
                      </div>
                    )}
                  </div>

                  {/* Right — from/to locations + qty + actions */}
                  <div className="w-full sm:w-72 shrink-0 space-y-2">
                    <div className="rounded-xl bg-slate-50 border border-slate-200 p-2.5 text-[12px]">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">From</div>
                      <div className="flex items-center gap-1.5 font-semibold text-slate-700 flex-wrap">
                        <ScanLine className="w-3.5 h-3.5" /> {r.pallet_id || '—'}
                        <span className="text-slate-400">·</span>
                        <MapPin className="w-3.5 h-3.5" />
                        {[r.from_zone, r.from_location_id].filter(Boolean).join(' · ') || '—'}
                      </div>
                      {r.from_warehouse_name && (
                        <div className="mt-0.5 text-[11px] text-slate-500 inline-flex items-center gap-1">
                          <Warehouse className="w-3 h-3" /> {r.from_warehouse_name}
                        </div>
                      )}
                    </div>
                    <div className="rounded-xl bg-amber-50 border border-amber-200 p-2.5 text-[12px]">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700">To</div>
                      <div className="flex items-center gap-1.5 font-semibold text-amber-900 flex-wrap">
                        <MapPin className="w-3.5 h-3.5" />
                        {[r.to_zone, r.to_location_id].filter(Boolean).join(' · ') || '—'}
                      </div>
                      {r.to_warehouse_name && (
                        <div className="mt-0.5 text-[11px] text-amber-700 inline-flex items-center gap-1">
                          <Warehouse className="w-3 h-3" /> {r.to_warehouse_name}
                        </div>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 text-right">
                      Qty on pallet: <span className="font-bold tabular-nums">{r.qty_snapshot}</span>
                    </div>

                    {r.status === 'pending' && (
                      <div className="flex gap-1.5 pt-1">
                        <button
                          type="button"
                          onClick={() => setRejectFor(r)}
                          className="flex-1 h-10 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-700 font-bold text-[13px] inline-flex items-center justify-center gap-1.5"
                          data-testid={`audit-queue-reject-${r.id}`}
                        >
                          <XCircle className="w-4 h-4" /> Reject
                        </button>
                        <button
                          type="button"
                          onClick={() => approve(r)}
                          disabled={approving === r.id}
                          className="flex-[1.4] h-10 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-[13px] inline-flex items-center justify-center gap-1.5 disabled:opacity-70"
                          data-testid={`audit-queue-approve-${r.id}`}
                        >
                          {approving === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                          Approve
                        </button>
                      </div>
                    )}
                    {r.status !== 'pending' && (
                      <div className="text-[11px] text-slate-500 text-right">
                        Resolved {fmtDt(r.resolved_at)} by {r.resolved_by || '—'}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {rejectFor && (
        <RejectModal
          row={rejectFor}
          API={API}
          authHeaders={authHeaders}
          onClose={() => setRejectFor(null)}
          onDone={load}
        />
      )}
    </AdminLayout>
  );
};

export default AdminAuditQueue;
